import type { TargetingCondition } from 'helpers/targeting-identity';
import { cn } from 'helpers/cn';

type ChipTone = 'plain' | 'add' | 'remove';

type ConditionChipsProps = {
  /** Only the key and label are read, so saved objectives' chips use it too. */
  conditions: readonly Pick<TargetingCondition, 'key' | 'label'>[];
  tone?: ChipTone;
  /** Show at most this many, then "+N more". */
  limit?: number;
};

const TONE_CLASS: Record<ChipTone, string> = {
  plain: 'border-line bg-field text-foreground',
  add: 'border-ok-line bg-ok-bg text-ok',
  remove: 'border-danger-line bg-danger-bg text-danger line-through',
};

const PREFIX: Record<ChipTone, string> = { plain: '', add: '+ ', remove: '' };

/** The Target Area's conditions as chips, with the same labels as the card. */
export const ConditionChips = ({ conditions, tone = 'plain', limit }: ConditionChipsProps) => {
  const shown = limit === undefined ? conditions : conditions.slice(0, limit);
  const hidden = conditions.length - shown.length;

  return (
    <span className="flex flex-wrap gap-1.5">
      {shown.map(({ key, label }) => (
        <span
          key={key}
          className={cn('max-w-full truncate rounded-md border px-2 py-0.5 font-mono text-micro', TONE_CLASS[tone])}
        >
          {PREFIX[tone]}
          {label}
        </span>
      ))}
      {hidden > 0 ? <span className="supporting self-center">+{hidden} more</span> : null}
    </span>
  );
};
